/**
 * aiModels.ts — the model list behind the "Model" dropdown in Automation & AI.
 *
 * Ids were copied from each provider's official models page on 2026-10-13:
 *  - Anthropic: https://platform.claude.com/docs/en/models/overview
 *  - OpenAI:    https://developers.openai.com/api/docs/models
 *  - Google:    https://ai.google.dev/gemini-api/docs/models
 *  - xAI:       https://docs.x.ai/developers/models
 *  - Meta:      ids are NOT published on the pages we could read, so only the
 *               id already configured as the Worker default is listed.
 * Providers retire models, so the dropdown also accepts any typed id, and an
 * unlisted id that is already saved keeps working and shows as "Custom".
 */
import { z } from "zod";
import type { AiProvider } from "./ai.ts";

export const aiModelOptionSchema = z.object({
  provider: z.enum(["anthropic", "openai", "google", "xai", "meta"]),
  id: z.string().min(1),
  label: z.string().min(1),
  note: z.string().optional(),
});
export type AiModelOption = z.infer<typeof aiModelOptionSchema>;

export const AI_MODEL_CATALOG: readonly AiModelOption[] = [
  { provider: "anthropic", id: "claude-fable-5-1", label: "Claude Fable 5.1", note: "Most capable" },
  { provider: "anthropic", id: "claude-opus-5-5", label: "Claude Opus 5.5", note: "Deep reasoning" },
  { provider: "anthropic", id: "claude-sonnet-5-5", label: "Claude Sonnet 5.5", note: "Balanced · default" },
  { provider: "anthropic", id: "claude-haiku-5-5", label: "Claude Haiku 5.5", note: "Fastest, cheapest" },
  { provider: "openai", id: "gpt-6-astra", label: "GPT-6 Astra" },
  { provider: "openai", id: "gpt-6.1-sol", label: "GPT-6.1 Sol", note: "Default" },
  { provider: "openai", id: "gpt-6-luna", label: "GPT-6 Luna" },
  { provider: "google", id: "gemini-3.8-flash", label: "Gemini 3.8 Flash", note: "Default" },
  { provider: "google", id: "gemini-3.6-flash", label: "Gemini 3.6 Flash" },
  { provider: "google", id: "gemini-3.5-flash-lite", label: "Gemini 3.5 Flash-Lite", note: "Cheapest" },
  { provider: "google", id: "gemini-3.1-pro-preview", label: "Gemini 3.1 Pro (preview)" },
  { provider: "google", id: "gemini-3.1-flash-lite", label: "Gemini 3.1 Flash-Lite" },
  { provider: "google", id: "gemini-3-flash-preview", label: "Gemini 3 Flash (preview)" },
  { provider: "google", id: "gemini-2.5-pro", label: "Gemini 2.5 Pro", note: "Older" },
  { provider: "google", id: "gemini-2.5-flash", label: "Gemini 2.5 Flash", note: "Older" },
  { provider: "xai", id: "grok-4.7", label: "Grok 4.7", note: "Default" },
  { provider: "xai", id: "grok-4.20", label: "Grok 4.20", note: "Older" },
  { provider: "meta", id: "muse-spark-1.3", label: "Muse Spark 1.3", note: "Id not confirmed in public docs" },
];

export function modelsForProvider(provider: AiProvider): AiModelOption[] {
  return AI_MODEL_CATALOG.filter((m) => m.provider === provider);
}

/** Which provider a model id belongs to: the catalog first, then the id's usual prefix. Null when it can't be told. */
export function providerForModel(modelId: string): AiProvider | null {
  const id = modelId.trim().toLowerCase();
  if (!id) return null;
  const known = AI_MODEL_CATALOG.find((m) => m.id.toLowerCase() === id);
  if (known) return known.provider;
  if (id.startsWith("claude")) return "anthropic";
  if (id.startsWith("gpt") || /^o\d/.test(id)) return "openai";
  if (id.startsWith("gemini")) return "google";
  if (id.startsWith("grok")) return "xai";
  if (id.startsWith("muse") || id.startsWith("llama")) return "meta";
  return null;
}
